import { type ReactNode } from "react";
import { type AliasesProps, getAliases, ProductAliases } from "~/domains/product/components/ProductAliases";
import { isKITKITProduct } from "~/domains/product/types/ProductData";
import { userIsConsumerDistributor, userIsDistributor } from "~/domains/user/utils/roles";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { WithT } from "i18next";
import { Parameter, type ParameterProps, ParameterStack } from "~/common/components/ParameterV2";

type ProductParamContext = {
  t: WithT["t"];
  product: ProductDescriptor;
};

export const ProductParams2 = {
  OldDesignation: ({ product, t }: ProductParamContext) => {
    if (!product.base.oldDesignation || isKITKITProduct(product)) {
      return null;
    }

    return {
      label: t("product.oldDesignations"),
      value: product.base.oldDesignation,
    };
  },
  SchaefflerId: ({ product, t }: ProductParamContext) => {
    if (!product.base.sapMaterialNumber || isKITKITProduct(product)) {
      return null;
    }

    return {
      label: t("product.schaefflerId"),
      value: product.base.sapMaterialNumber,
    };
  },
  GPSD: ({ product, t }: ProductParamContext) => {
    if (!product.base.gpsdGroup || isKITKITProduct(product)) {
      return null;
    }
    if (!userIsDistributor() && !userIsConsumerDistributor()) {
      return null;
    }

    return {
      label: t("common.product.gpsdGroup"),
      value: product.base.gpsdGroup,
    };
  },
  ProductAliases:
    (config: Omit<AliasesProps, "product">) =>
    ({ product, t }: ProductParamContext) => {
      if (isKITKITProduct(product)) {
        return null;
      }

      if (getAliases(product.base, config.mode ?? "all", config.packCode).length === 0) {
        return null;
      }

      return {
        label: t("product.customerId"),
        value: <ProductAliases product={product.base} {...config} />,
      };
    },
  CountryOfOrigin: ({ product, t }: ProductParamContext) => {
    if (isKITKITProduct(product)) {
      return null;
    }

    const countries = product.base.fifteenDigit
      ? Array.from(new Set(Object.values(product.base.fifteenDigit).map((d) => d.countryOfOrigin))).filter(Boolean)
      : [];

    if (countries.length === 0) {
      return null;
    }

    return {
      label: t("product.countryOfOrigin"),
      value: countries.join(", "),
    };
  },
  TariffNumber: ({ product, t }: ProductParamContext) => {
    if (isKITKITProduct(product)) {
      return null;
    }

    const tariffNumbers = product.base.fifteenDigit
      ? Array.from(new Set(Object.values(product.base.fifteenDigit).flatMap((d) => d.customsData))).filter(Boolean)
      : [];

    if (tariffNumbers.length === 0) {
      return null;
    }

    return {
      label: t("product.tariffNumber"),
      value: tariffNumbers.join(", "),
    };
  },
} as const;

type Param = {
  label: ReactNode;
  value: ReactNode;
};
interface ParametersProps {
  data: Param[];
  showEmpty?: boolean;
  emptySymbol?: ReactNode;
  parameterVariant?: ParameterProps["variant"];
}

export const Parameters = ({ data, showEmpty = false, emptySymbol = "-", parameterVariant }: ParametersProps) => {
  if (!showEmpty) {
    data = data.filter((param) => !!param.value);
  }
  if (data.length === 0) {
    return null;
  }
  return (
    <ParameterStack parameterVariant={parameterVariant}>
      {data.map((param, index) => (
        <Parameter key={index} label={param.label} value={param.value ?? emptySymbol} />
      ))}
    </ParameterStack>
  );
};
